// ═══════════════════════════════════════════════════════════════════════════
// proximoPaso.js — la sección "Próximo paso" de toda card de debugging.
// (ADR 2026-08-21 el-proximo-paso-es-obligatorio-en-toda-card-de-debugging)
// ═══════════════════════════════════════════════════════════════════════════
// Mariano: *"hay 124 bugs en revisión que tengo que adivinar cuál es el próximo
// paso"*. La regla que fijó: NUNCA puede no haber próximo paso — salvo `nuevo`,
// que es la cola que ficha la guardia esa misma noche.
//
// Formato fijo, calibrado por él con ejemplos: explicación de ~300 caracteres
// en lenguaje llano (qué pasa → qué implica → qué se propone) + hasta TRES
// opciones, la sugerida marcada, cada una con su porqué en una línea.
//
// Se DERIVA en lectura, cero migración — mismo patrón que `tagEfectivo`. Para
// los cuatro estados asistidos (ESTADOS_ASISTIDOS, espejo de la hoja
// functions/lib/proximoPasoOpciones.js — paridad en el test de esta lib) la
// fuente rica es el subobjeto `asistente` que escribe el backend cuando
// existe; esta tabla es el FALLBACK, y por diseño la card funciona entera con
// el flag del asistente apagado — condición de forense contra la clase
// candado-decorativo.
//
// Las `accion` de las opciones son un vocabulario CERRADO que consume la card
// (mapea a callables/transiciones existentes). El grupo (`grupoDe`) alimenta
// el filtro principal por ACCIÓN: qué venís a hacer, no en qué estado está.
// ═══════════════════════════════════════════════════════════════════════════

// Vocabulario cerrado de acciones que la card sabe ejecutar. Cada una mapea a
// un camino que YA existe (callable o transición del motor); esta lib no
// inventa operaciones nuevas.
export const ACCIONES = [
  'aprobar',            // informe → nuevo (Puerta Cero, wizard + step-up del destino `nuevo`)
  'rechazar',           // informe → descartado (motivo obligatorio)
  'a_mesa',             // → en_mesa (lo decide Mariano con calma)
  'a_comite',           // en_mesa → en_comite (flujo v2: lo piensan los agentes, sin pasar por Mariano)
  'a_investigar',       // informe → en_investigacion (es consulta, no bug)
  'promover',           // informe → tarjeta en el roadmap (callable propio)
  'a_guardia',          // en_revision/cola_humana/escalado → nuevo (que lo arregle la guardia)
  'quedo_bien',         // validables → cerrado
  'cerrar',             // en_mesa → cerrado (flujo v2, 3-sep: el comité propone cerrar y Mariano confirma con un click)
  'sigue_con_errores',  // validables/cierres → sigue_con_errores (vuelve esta noche)
  'archivar',           // cierres → archivado
  'reabrir',            // archivado → nuevo
  'descartar',          // → descartado
]

// Los grupos del filtro principal: QUÉ VENÍS A HACER. No confundir con
// ESTADOS_TERMINALES/PARKED/EN_VUELO de `functions/lib/estadosDebug.js` —
// aquellos responden "¿el organismo lo cuenta como vivo?"; estos responden
// "¿quién tiene que actuar?". Nombrarlos distinto es a propósito (architect).
export const GRUPOS_ACCION = ['aprobar', 'decidir', 'probar', 'archivar', 'en_marcha']

const OP = (accion, label, porque, sugerida = false) => ({ accion, label, porque, sugerida })

// ── La tabla ────────────────────────────────────────────────────────────────
// Una entrada por estado. `explicacion` ≤ 320 chars, lenguaje llano, sin
// nombres de código. `opciones`: máx 3 (excepciones declaradas en el test),
// EXACTAMENTE una sugerida — ninguna en los de `en_marcha` (cola_humana).
// `grupo`: a qué filtro de acción pertenece el reporte parado en ese estado.
const TABLA = {
  informe: {
    grupo: 'aprobar',
    decision:   'La decisión: ¿esto entra a la cola de corrección? Si aprobás, la guardia lo toma esta misma noche. Si es una consulta y no un arreglo, mandalo a investigar.',
    opciones: [
      OP('aprobar', 'Aprobar', 'entra a la cola y la guardia lo toma esta noche', true),
      OP('rechazar', 'Rechazar', 'no vale la pena — queda registrado con tu motivo'),
      OP('a_investigar', 'A investigar', 'es una pregunta, no un bug: la responde el investigador'),
      // Cuarta opción y única excepción al tope de 3 (22-ago): al fusionar el
      // bloque viejo en la card, "Promover a roadmap" quedó INALCANZABLE — su
      // único botón vivía en el bloque muerto. Lo cazó el adversarial
      // (Claude-fisc, run 24961ce). Es el camino de los pedidos de
      // funcionalidad: informe → en_mesa → planificado → archivado + tarjeta
      // en el board, sin pasar por `nuevo` ni sellar decisionHumana.
      OP('promover', 'Promover a roadmap', 'es un pedido de funcionalidad: va al backlog, no a la cola de bugs'),
    ],
  },
  nuevo: null, // el ÚNICO sin próximo paso: lo ficha la guardia esta noche.
  en_revision: {
    grupo: 'decidir',
    decision:   'La decisión: ¿esto necesita manos o criterio tuyo? Lo que necesita manos va a la guardia; lo que necesita tu criterio, a tu mesa.',
    opciones: [
      OP('a_guardia', 'Que lo arregle la guardia', 'es trabajo de ingeniería, no una decisión tuya', true),
      OP('a_mesa', 'Lo decido yo', 'queda en tu lista de decisiones, fuera de la cola operativa'),
      OP('descartar', 'Descartar', 'no vale la pena'),
      // 23-ago (regla de Mariano): lo que se trabaja ENTRE él y el agente —
      // features, permisos, lo que la guardia sola no puede ejecutar — va al
      // roadmap, donde él lo agarra y lo prioriza ("now" si es corrección).
      // El callable ya banca este origen: el motor permite el hop a en_mesa.
      OP('promover', 'Promover a roadmap', 'se trabaja juntos: va al board y lo priorizás vos'),
    ],
  },
  // 29-sep-2026 (pedido de Mariano: «que DECIDIR diga 0 y todo esté en
  // desarrollo»): `cola_humana` deja de ser una decisión suya. Desde el ADR
  // 2026-09-06 (decisión 3) es la cola de la SESIÓN DE DÍA: ahí van los
  // aprobados de carril humano, y ahí manda la mañana lo que no pudo cerrar o
  // verificar sola (inconclusos, render, infra, docs sin origen). Lo técnico lo
  // resuelve esa sesión; lo que pide una definición del dueño lo pasa a
  // `en_mesa`, que sigue siendo «Decisión tuya» (ADR 2026-09-02). Por eso es
  // del grupo `en_marcha`, con su propia palabra en la fila del sistema
  // (`sesion_dia`), y sin sugerida: la acción correcta para Mariano es no hacer
  // nada. Sale también de ESTADOS_ASISTIDOS: la lectura del asistente es para
  // las colas de decisión, y acá taparía este texto con un «qué hacer» y un
  // botón verde (compuerta 2 de este cambio: forense, clase
  // camino-manual-olvidado). Enmienda en los ADR 2026-08-21 y 2026-08-23.
  cola_humana: {
    grupo: 'en_marcha',
    decision:   'Está en la cola de la sesión de día: la guardia no puede o no pudo terminarlo sola y lo sigue esa sesión. Acá no tenés que decidir nada; si hace falta tu firma o que prendas algo, te lo piden aparte. Si en realidad pide una definición tuya, pasalo a tu mesa.',
    opciones: [
      OP('a_mesa', 'Lo decido yo', 'pide una definición tuya: queda en tu mesa'),
      OP('a_guardia', 'Que lo haga la guardia', 'si no toca nada vedado: vuelve a la cola como caso nuevo'),
      OP('descartar', 'Descartar', 'no vale la pena'),
      OP('promover', 'Promover a roadmap', 'se trabaja juntos: va al board y lo priorizás vos'),
    ],
  },
  escalado: {
    grupo: 'decidir',
    decision:   'El agente agotó sus salidas y te lo devuelve. La decisión es el camino: el porqué del escalamiento está en el último comentario.',
    opciones: [
      OP('a_mesa', 'Lo decido yo', 'es una definición tuya, con calma', true),
      OP('a_guardia', 'Que lo intente la guardia', 'si el bloqueo ya no existe'),
      OP('descartar', 'Descartar', 'no vale la pena'),
      OP('promover', 'Promover a roadmap', 'se trabaja juntos: va al board y lo priorizás vos'),
    ],
  },
  en_mesa: {
    grupo: 'decidir',
    decision:   'Ya está en tu lista de decisiones. Cuando resuelvas, mandalo a la guardia con tu decisión escrita; si ya está resuelto, cerralo; si es diseño técnico y no una decisión tuya, que lo piense el comité; o descartalo si al final no va.',
    opciones: [
      OP('a_guardia', 'Decidido — a la guardia', 'tu decisión queda escrita y la ejecutan esta noche', true),
      // Flujo v2 (3-sep): el comité NO cierra (mitigación de seguridad) — te
      // propone cerrar y este botón es tu click. También es la salida de
      // "te toca a vos afuera" cuando ya lo hiciste.
      OP('cerrar', 'Cerrar — ya está resuelto', 'el arreglo ya está hecho o el caso ya no aplica: queda cerrado, reabrible'),
      OP('a_comite', 'Que lo piense el comité', 'es diseño técnico: los agentes convergen y te enterás por el parte'),
      OP('descartar', 'Descartar', 'decidiste que no va'),
      // 5-sep (pedido Mariano, report F8Yhd14dBFhaTEzMivK2): "esta acción donde
      // tengo que hacer algo yo debería tener promover a roadmap". El callable
      // arranca la cadena desde en_mesa (HOPS_PROMOCION).
      OP('promover', 'Promover a roadmap', 'se trabaja juntos: va al board y lo priorizás vos'),
    ],
  },
  en_comite: null,         // flujo v2: lo piensan los agentes — sin botones, cae a DEFAULT_EN_MARCHA
  en_curso: null,          // en movimiento — ver DEFAULT_EN_MARCHA
  ajustado_ia: null,
  deployado: null,
  en_verificacion: null,
  en_investigacion: null,
  refutado: null,
  sigue_con_errores: null,
  planificado: null,
  // 23-oct (Mariano): ver `en_proyecto` en ETAPA_POR_ESTADO. Caía a
  // DEFAULT_EN_MARCHA, que afirma «avanza solo». No dice QUÉ proyecto: ningún
  // dato lo ata, sólo el motivo si quien lo movió lo dejó. Grupo `en_marcha`,
  // igual que functions/lib/gruposAccion.js.
  diferido: {
    grupo: 'en_marcha',
    decision: 'Lo absorbe un proyecto del roadmap: los agentes no lo tocan, y se arregla cuando ese proyecto se trabaje en Desarrollo. Si quien lo movió dejó el motivo, el hilo dice cuál. Si querés sacarlo del proyecto, podés mandarlo a tu mesa o descartarlo.',
    opciones: [
      OP('a_mesa', 'Lo decido yo', 'lo saca del proyecto y vuelve a tu mesa'),
      OP('descartar', 'Descartar', 'no vale la pena seguir'),
    ],
  },
  en_validacion: {
    grupo: 'probar',
    decision:   'El arreglo ya está en la app. La decisión: ¿quedó bien? Con tu confirmación se cierra; si sigue fallando, vuelve al agente esta noche.',
    opciones: [
      OP('quedo_bien', 'Quedó bien', 'cierra el caso', true),
      OP('sigue_con_errores', 'Sigue con errores', 'vuelve al agente esta noche, solo'),
    ],
  },
  en_uat: {
    grupo: 'probar',
    decision:   'Te toca probarlo. Si en 7 días nadie responde, se cierra solo y se puede reabrir con un click.',
    opciones: [
      OP('quedo_bien', 'Quedó bien', 'cierra el caso', true),
      OP('sigue_con_errores', 'Sigue con errores', 'vuelve al agente esta noche'),
    ],
  },
  verificado_auto: {
    grupo: 'probar',
    decision:   'La guardia verificó sola que funciona, con evidencia. Falta tu confirmación — un vistazo alcanza.',
    opciones: [
      OP('quedo_bien', 'Quedó bien', 'confirmás y se cierra', true),
      OP('sigue_con_errores', 'Sigue con errores', 'la verificación no alcanzó'),
    ],
  },
  cerrado: {
    grupo: 'archivar',
    decision:   'Ya está resuelto — sólo falta sacarlo de la vista. Si no lo archivás, se archiva solo a los 7 días.',
    opciones: [
      OP('archivar', 'Archivar', 'lo saca de todas las listas', true),
      OP('sigue_con_errores', 'Reabrir', 'si en realidad no quedó bien'),
    ],
  },
  cerrado_sin_validar: {
    grupo: 'archivar',
    decision:   'Se cerró sin validación independiente: o venció el plazo de quien lo reportó, o el arreglo era de un reporte de la IA y no había prueba para confirmarlo. Merece tu vistazo antes de archivar: si no quedó bien, se reabre con un click.',
    opciones: [
      OP('archivar', 'Archivar', 'lo diste por bueno', true),
      OP('sigue_con_errores', 'Reabrir', 'no quedó bien — vuelve al agente'),
    ],
  },
  cerrado_explicado: {
    grupo: 'archivar',
    decision:   'Era una consulta y fue respondida — la explicación está en el hilo. Sólo falta archivarlo.',
    opciones: [OP('archivar', 'Archivar', 'la respuesta queda en el hilo', true)],
  },
  descartado: {
    grupo: 'archivar',
    decision:   'Se decidió no hacerlo, con su motivo registrado. Sólo falta archivarlo.',
    opciones: [OP('archivar', 'Archivar', 'fuera de la vista', true)],
  },
  archivado: {
    grupo: 'archivar',
    decision:   'Fuera de todas las listas. Sólo vuelve si lo reabrís.',
    opciones: [OP('reabrir', 'Reabrir', 'vuelve a la cola como caso nuevo', true)],
  },
}

// Los estados EN MARCHA comparten explicación: los agentes los están
// trabajando y el próximo paso tuyo es no hacer nada. La regla de Mariano
// dice que igual tiene que HABER sección — con la opción de intervenir.
const DEFAULT_EN_MARCHA = {
  grupo: 'en_marcha',
  decision: 'Está en manos de los agentes y avanza solo — no hace falta nada tuyo. Si querés sacarlo del circuito igual, podés mandarlo a tu mesa o descartarlo.',
  opciones: [
    OP('a_mesa', 'Lo decido yo', 'lo saca del circuito automático'),
    OP('descartar', 'Descartar', 'no vale la pena seguir'),
  ],
}

/**
 * PURO. Recorta un texto en un límite SIN partir palabras, sanitizando lo
 * mínimo para una card (backticks y negritas de markdown afuera, whitespace
 * colapsado). `…` sólo si de verdad recortó.
 *
 * Compartido a propósito: `lineaRapida` tenía su propio recorte inline que
 * partía palabras — dos recortadores en la misma lib divergen (consigna:
 * la duplicación encontrada en el camino del cambio se unifica).
 */
export function recorteLimpio(texto, max = 200, piso = 120) {
  const limpio = String(texto || '').replace(/[`*]/g, '').replace(/\s+/g, ' ').trim()
  if (!limpio) return ''
  if (limpio.length <= max) return limpio
  const corte = limpio.lastIndexOf(' ', max)
  return `${limpio.slice(0, corte > piso ? corte : max)}…`
}

// Decisión de Mariano (27-ago-2026): el "Próximo paso" entrega el texto ENTERO
// — *"no lo cortes, que entregue lo que trae"*. Quedan el saneo (markdown,
// whitespace) y NINGÚN tope: el único recorte vivo en la lib es el de la línea
// de tabla (`lineaRapida`), que es de UNA fila por diseño. Mismo recortador,
// tope infinito — un segundo sanitizador divergiría (consigna: unificar).
const enteroLimpio = (t) => recorteLimpio(t, Infinity)

/**
 * PURO. LA SITUACIÓN del caso — qué está pasando ACÁ, no en el estado en
 * general. Pedido de Mariano (22-ago): *"'que lo arregle la guardia' está
 * bien, ¿pero QUÉ tiene que arreglar la guardia?"*.
 *
 * Cadena de fallback, del texto más pensado al más crudo. Todo reporte real
 * tiene al menos `descripcion`, así que para la cola viva nunca queda vacía —
 * y MEJORA SOLA: el asistente (flag ON, 25/noche) va escribiendo el resumen
 * específico de cada `en_revision`, y los informes nuevos traen su párrafo.
 *
 * `null` sólo para objetos sin nada (fixtures) — la card no pinta el bloque.
 */
export function situacionDe(r) {
  // CRIOLLO-ONLY (Mariano, 22-ago: *"en el próximo paso te ponés técnico, yo
  // no quiero que te pongas técnico"*). `triage.linea` y `descripcion` quedaron
  // FUERA de la cadena a propósito: para lo técnico está el expediente abajo.
  // Si no hay texto simple, la card lo dice y ofrece generarlo — mentir con
  // jerga no es un fallback, es el problema que esto vino a arreglar.
  //
  // Sin topes (27-ago-2026): cada candidato se entrega entero — el orden de
  // la cadena sigue siendo del texto más pensado al más crudo.
  const candidatos = [
    r?.enCriollo?.parrafo,   // el texto de lectura rápida (informes nuevos)
    // En un estado asistido, una lectura escrita para OTRO estado se salta: el
    // batch la rehace (report KG1iMh6Sl0ARlQ2TcS5A). Fuera de ellos —p. ej.
    // `cola_humana`— nadie la regenera, y el resumen describe el caso, no el
    // estado: se conserva.
    (ESTADOS_ASISTIDOS.includes(r?.estado) && !lecturaEsDeEsteEstado(r)) ? null : r?.asistente?.resumen,   // el análisis del asistente (en_revision)
    r?.enCriollo?.detalle,   // el expediente en criollo (backfill + crons)
    r?.enCriollo?.resumen,   // la línea de tabla
  ]
  for (const c of candidatos) {
    const t = enteroLimpio(c)
    if (t) return t
  }
  return null
}

/**
 * PURO. La sección "Próximo paso" para un reporte, o `null` SÓLO para `nuevo`.
 *
 * Dos partes, por pedido de Mariano: `situacion` (qué pasa en ESTE caso —
 * `situacionDe`) y `decision` (qué hay que decidir — la tabla). El asistente,
 * cuando existe, aporta la situación Y marca la sugerida; la tabla es el
 * fallback SIEMPRE presente: la card no depende de ningún flag.
 */
export function proximoPasoDe(r) {
  const estado = r?.estado
  if (!estado || estado === 'nuevo') return null
  const base = TABLA[estado] === undefined ? DEFAULT_EN_MARCHA : TABLA[estado]
  const entradaTabla = base === null ? DEFAULT_EN_MARCHA : base
  // 7-sep (ADR 2026-09-06 carril humano): el caso quedó clasificado en zona
  // vedada para agentes — "que lo arregle la guardia" no es una opción (el
  // motor la rechaza), y la aprobación lo manda a cola_humana. La card lo dice.
  // 15-sep (report RNy7tbsVSPJlJkXRJotl): la card afirmaba "el arreglo toca
  // zona vedada", y eso no se sabe. La clasificación sale de los paths que el
  // reporte MENCIONA en su evidencia, y un reporte puede citar una regla como
  // fundamento con el arreglo entero en otro lado — medido en 4 de los 7 casos
  // vivos con locus mixto. Mismo cambio que el `motivo` del guard hermano
  // (functions/debuggingMotor.js:364): se dice la clasificación, no la causa.
  // 16-sep (mismo report, dictamen del comité): quien crea el informe ya puede
  // declarar qué paths CITA como regla y no va a modificar, y el carril los
  // descuenta. El texto lo dice para que no quede afirmando que la distinción
  // no existe — los dos emisores se corrigen juntos, que es el bloqueo que
  // `forense` puso sobre el PR #2450 (clase `camino-manual-olvidado`).
  // 17-sep (report ybUU6rlRUD5u0nXwWQI6): el predicado YA NO SE COMPUTA ACÁ.
  // Esta línea decía `r?.triage?.carril === 'humano'` y era una SEGUNDA
  // implementación, más angosta que la del motor: `carrilHumanoDe`
  // (functions/debuggingMotor.js:350) pregunta eso O si el locus toca zona
  // vedada sin declararla como cita. Por el hueco entre las dos, la card
  // ofrecía —y en 11 de 14 casos vivos marcaba SUGERIDO— un botón que el motor
  // rechaza con `failed-precondition`. Ahora el server lo deriva y manda el
  // booleano en la proyección (`functions/debugging.js`), igual que `tag` y
  // `area`; acá sólo se consume.
  //
  // ⚠️ Se pregunta por `=== true`, no por `!== false`, y la diferencia importa:
  // el campo es TRI-ESTADO (true / false / ausente en una pestaña vieja o
  // durante la ventana de deploy, donde hosting sale ~5 min antes que
  // functions — molde del mismo caso en `debugReports.js:786-788`). Con el
  // complemento, la ausencia se leería como "es carril humano" y la card
  // pasaría a AFIRMARLE a Mariano que el caso quedó clasificado en zona vedada
  // en el 100 % de los reportes durante esa ventana. Esconder un botón de más
  // es conservador; afirmar un hecho que el sistema no sabe, no — y es la misma
  // clase que el report RNy7tbsVSPJlJkXRJotl cerró en estas mismas líneas.
  // Con `=== true` la ausencia degrada al comportamiento de HOY, acotado a esa
  // ventana, y el predicado viejo no sobrevive en ningún lado.
  // Que el campo no se caiga de la proyección en silencio lo cuidan dos guards
  // mecánicos: `proximoPaso.carrilHumano.guard.test.js` (que en `src/**` no
  // vuelva a haber recómputo, y que esta línea siga consumiéndolo) y
  // `debuggingOrigenProyectado.guard.test.js` del lado del productor.
  const carrilHumano = r?.carrilHumano === true
  // La ACLARACIÓN se aplica al FINAL, no acá dentro. Hasta hoy viajaba dentro de
  // `entrada.decision`, y la rama del asistente de más abajo hace
  // `decision: queHacer || comun.decision` — o sea que la tiraba ENTERA cuando el
  // asistente había escrito `queHacer`, que es el caso más frecuente. El aviso
  // sólo se veía, entonces, cuando NO había lectura del asistente. Se aplica
  // después de elegir el texto, en los DOS caminos, para que el próximo campo que
  // gane precedencia no la vuelva a tirar (report ybUU6rlRUD5u0nXwWQI6).
  const conAclaracion = (texto) => (carrilHumano
    ? `${texto} (Quedó clasificado en zona vedada para agentes, por un archivo que el caso menciona y que nadie declaró como cita: lo aplica una persona, no la guardia.)`
    : texto)
  const entrada = carrilHumano
    ? { ...entradaTabla, opciones: entradaTabla.opciones.filter((o) => ejecucionDe(o.accion)?.estado !== DESTINO_QUE_EL_CARRIL_HUMANO_CIERRA) }
    : entradaTabla

  const comun = { estado, grupo: entrada.grupo, situacion: situacionDe(r), decision: entrada.decision }

  if (ESTADOS_ASISTIDOS.includes(estado) && r?.asistente?.resumen && lecturaEsDeEsteEstado(r)) {
    // La recomendación v2 ES una acción de la tabla (la hoja manda — ADR
    // 2026-08-23); las lecturas v1 traen el enum viejo y se mapean. Si la
    // acción no existe en las opciones de ESTE estado (lectura sin el sello
    // `paraEstado`, o valor desconocido), no se fuerza: quedan las sugeridas
    // default de la tabla.
    const rec = LEGACY_RECOMENDACION[r.asistente.recomendacion] || r.asistente.recomendacion
    const sug = entrada.opciones.some((o) => o.accion === rec) ? rec : null
    const opciones = entrada.opciones.map((o) => ({ ...o, sugerida: sug ? o.accion === sug : o.sugerida }))
    // La DECISIÓN del caso, no la del estado (Mariano, 22-ago: *"con leer
    // próximo paso tengo que saber cuál es el problema y QUÉ vas a hacer"*).
    // `queHacer` es lo que el asistente propone hacer con ESTE caso; si no lo
    // escribió (puede venir vacío), cae la pregunta de la tabla.
    const queHacer = enteroLimpio(r.asistente.queHacer)
    return {
      ...comun,
      decision: conAclaracion(queHacer || comun.decision),
      queToca: Array.isArray(r.asistente.queToca) ? r.asistente.queToca.slice(0, 5) : [],
      // v3: zona crítica y choque de regla — párrafos en criollo, null si el
      // material no los mostró (el server ya los pasó por el guard de jerga;
      // el recorte acá es el cinturón de la card, mismo criterio que situacion).
      zonaCritica: enteroLimpio(r.asistente.zonaCritica) || null,
      choqueRegla: enteroLimpio(r.asistente.choqueRegla) || null,
      // v5: quién ejecuta el arreglo. Enum cerrado también acá (el server ya
      // valida, pero la card no confía en docs viejos o escritos por otra
      // vía): cualquier otro valor cae a null y la card no promete nada.
      quienEjecuta: QUIENES_EJECUTA.includes(r.asistente.quienEjecuta) ? r.asistente.quienEjecuta : null,
      manoTuya: enteroLimpio(r.asistente.manoTuya) || null,
      // `porqueSugerida` explica POR QUÉ apretar la sugerida, y el asistente lo
      // escribió para SU recomendación: tiene sujeto sólo si la sugerida rendida
      // ES esa (`sug`). Si la recomendación no es botón de este estado —filtrada
      // por el carril humano, `sin_opinion`, o escrita para otro estado— las
      // sugeridas quedan en el default de la tabla, y en `escalado` o `informe`
      // ese default sobrevive: el porqué quedaba colgado de OTRO botón (report
      // 1biPuUp8UKYqtLQ3rtRp; antes, el huérfano de ybUU6rlRUD5u0nXwWQI6).
      porqueSugerida: (sug && r.asistente.porque) ? String(r.asistente.porque) : null,
      opciones,
      // Los CAMINOS del caso (v2): 2-3 alternativas con su porqué, la sugerida
      // marcada. Son TEXTO para elegir — jamás se ejecutan; lo ejecutable
      // sigue siendo el vocabulario cerrado de `opciones`.
      opcionesCaso: opcionesCasoDe(r.asistente),
      sugeridaCaso: sugeridaCasoDe(r.asistente),
      fuente: 'asistente',
    }
  }
  return { ...comun, decision: conAclaracion(comun.decision), queToca: [], zonaCritica: null, choqueRegla: null, quienEjecuta: null, manoTuya: null, porqueSugerida: null, opciones: entrada.opciones, opcionesCaso: [], sugeridaCaso: null, fuente: 'tabla' }
}

// Lo que el motor rechaza con carril humano es el DESTINO, no el nombre de la
// acción (`guardCarrilHumano`, functions/debuggingMotor.js: el paso a `nuevo`,
// para cualquier actor). La card filtra por ese destino, leído de `ejecucionDe`
// (la misma tabla que ejecuta el botón): una acción futura que también mande el
// caso a `nuevo` queda filtrada sin tocar esta línea (report gjpNJIqxWNFczUg4WbF1).
const DESTINO_QUE_EL_CARRIL_HUMANO_CIERRA = 'nuevo'

// Los estados donde el asistente escribe lectura — espejo de la hoja
// `functions/lib/proximoPasoOpciones.js` (paridad en el test, molde
// gruposAccion). Inclusión explícita, jamás por resta. `cola_humana` salió el
// 29-sep: es la cola de la sesión de día, no una decisión (ver su entrada en
// la tabla); una lectura vieja que haya quedado en esos docs sólo aporta su
// `resumen` como situación (situacionDe) y nada más.
export const ESTADOS_ASISTIDOS = ['informe', 'en_revision', 'escalado', 'en_mesa']

// ¿La lectura del asistente se escribió para el estado ACTUAL? Espejo de
// `lecturaEsDeEsteEstado` de la hoja `functions/lib/proximoPasoOpciones.js`
// (paridad en el test). Las opciones, la sugerida y la clase de la lectura
// valen para el estado en que se escribió (`asistente.paraEstado`); sin el
// sello —lecturas anteriores— cuenta como de este estado (report KG1iMh6Sl0ARlQ2TcS5A).
export function lecturaEsDeEsteEstado(r) {
  const para = r?.asistente?.paraEstado
  return !para || para === r.estado
}

// Compat con lecturas v1 (enum viejo del prompt, pre 23-ago). Misma tabla que
// `LEGACY_RECOMENDACION` de functions/lib/proximoPasoOpciones.js (la hoja) —
// la paridad la fija el test junto con las opciones.
const LEGACY_RECOMENDACION = { nuevo: 'a_guardia', descartar: 'descartar', promover: 'promover' }

// v5 — el enum de quién ejecuta, espejo del server (lib/triage/decision.js).
const QUIENES_EJECUTA = ['vos', 'agente', 'juntos']

/** PURO. Los caminos del caso, saneados para la card: hasta 3, con texto. */
export function opcionesCasoDe(asistente) {
  if (!Array.isArray(asistente?.opciones)) return []
  return asistente.opciones
    .filter((o) => o && typeof o.texto === 'string' && o.texto.trim())
    .slice(0, 3)
    .map((o, i) => ({ id: String(o.id || 'abc'[i] || i), texto: enteroLimpio(o.texto), porque: enteroLimpio(o.porque) }))
}

/** PURO. El id del camino sugerido — sólo si sobrevivió al saneo. */
export function sugeridaCasoDe(asistente) {
  const id = asistente?.sugeridaCaso
  if (!id) return null
  return opcionesCasoDe(asistente).some((o) => o.id === String(id)) ? String(id) : null
}

/**
 * PURO. El grupo de acción de un reporte — alimenta el filtro principal.
 * `nuevo` y los en-marcha caen en 'en_marcha': no hay nada que hacer con ellos.
 */
export function grupoDe(r) {
  const estado = r?.estado
  if (!estado || estado === 'nuevo') return 'en_marcha'
  const base = TABLA[estado] === undefined ? DEFAULT_EN_MARCHA : TABLA[estado]
  return (base === null ? DEFAULT_EN_MARCHA : base).grupo
}

/**
 * PURO. Cuenta reportes por grupo de acción — alimenta las cards del head de
 * /debugging. Vive acá y no en la página para que la paridad card ↔ filtro sea
 * por CONSTRUCCIÓN (misma `grupoDe` que usa `filtrarReportes`) y testeable sin
 * montar React. Los cinco grupos siempre están presentes, en cero si no hay.
 */
export function conteoPorGrupo(reportes) {
  const c = { aprobar: 0, decidir: 0, probar: 0, en_marcha: 0, archivar: 0 }
  for (const r of reportes || []) c[grupoDe(r)] += 1
  return c
}

// Para el drift-guard: la lista de estados que esta tabla conoce EXPLÍCITO.
export const ESTADOS_CON_ENTRADA = Object.keys(TABLA)

// ── La fila del SISTEMA (Mariano, 4-sep-2026): dónde está cada cosa ─────────
// La cabecera de /debugging se parte en dos: lo que le toca a una persona
// (aprobar · decidir · probar — los grupos de acción) y, debajo, discreto, por
// dónde viaja lo demás. Las ETAPAS son el orden de viaje de un arreglo:
// cola → trabajando → deployado → verificando (+ comité, aparte) → cerrados.
// Por INCLUSIÓN explícita (forense gauge-por-resta): todo estado de los grupos
// en_marcha/archivar tiene etapa, y el test lo exige — un estado sin palabra
// es trabajo vivo que ninguna vista muestra (la clase que el 22-ago ya pagó).
// 29-sep: `sesion_dia` es la cola de la sesión de día (`cola_humana`). No es
// «el sistema» en sentido estricto —la trabaja una persona—, pero tampoco le
// toca a Mariano: por eso vive en esta fila y no en las cards de arriba.
// El mapa tiene DOS consumidores desde el 29-sep (la cabecera y el digest, que
// cuenta la sesión de día): la hoja `functions/lib/gruposAccion.js` tiene el
// mismo mapa y el test de paridad los ata en los dos sentidos.
// `en_proyecto` (23-oct) va entre Comité y Sesión de día: como esas dos, queda
// fuera del viaje del arreglo.
export const ETAPAS = ['en_cola', 'trabajando', 'deployado', 'verificando', 'comite', 'en_proyecto', 'sesion_dia', 'cerrados']

export const ETAPA_POR_ESTADO = Object.freeze({
  // lo que la guardia TOMA (ORIGENES_TOMABLES del MCP): nuevo y sigue_con_errores
  nuevo: 'en_cola', sigue_con_errores: 'en_cola',
  // en manos de los agentes. Decisión Mariano 4-sep: planificado/diferido/refutado
  // acá. `planificado` se queda aunque sea parked: es un paso de la promoción a
  // roadmap (`en_mesa → planificado → archivado`, HOPS_PROMOCION en
  // functions/debugging.js) que el archivador cierra solo
  // (ORIGENES_ARCHIVADO_SISTEMA en functions/debuggingMotor.js) — no se acumula.
  en_curso: 'trabajando', ajustado_ia: 'trabajando', en_investigacion: 'trabajando',
  refutado: 'trabajando', planificado: 'trabajando',
  // 23-oct (Mariano): lo absorbe un proyecto del roadmap. Contado en «Trabajando»
  // afirmaba trabajo que nadie hace: ningún agente puede tocarlo
  // (ESTADOS_PARKED en `origenDeAgente`, functions/debuggingMotor.js).
  diferido: 'en_proyecto',
  deployado: 'deployado',
  en_verificacion: 'verificando',
  en_comite: 'comite',
  cola_humana: 'sesion_dia',
  cerrado: 'cerrados', cerrado_sin_validar: 'cerrados', cerrado_explicado: 'cerrados', descartado: 'cerrados', archivado: 'cerrados',
})

export const ETAPA_META = Object.freeze({
  en_cola:     { label: 'En cola',     sub: 'la guardia' },
  trabajando:  { label: 'Trabajando',  sub: 'en curso · ajustado' },
  deployado:   { label: 'Deployado',   sub: 'sala de espera' },
  verificando: { label: 'Verificando', sub: 'la mañana' },
  comite:      { label: 'Comité',      sub: 'pensando' },
  en_proyecto: { label: 'En un proyecto', sub: 'lo absorbe un proyecto del roadmap' },
  sesion_dia:  { label: 'Sesión de día', sub: 'lo que la guardia no pudo sola' },
  cerrados:    { label: 'Cerrados',    sub: 'bandeja de repaso' },
})

/** PURO. Etapa de un reporte, o null si su estado no tiene (los de persona: informe/decidir/probar). */
export function etapaDe(r) {
  return ETAPA_POR_ESTADO[r?.estado] ?? null
}

/**
 * PURO. Cuenta por etapa — todas las ETAPAS siempre presentes. `cerrados` descuenta los
 * `archivado` (como la card de hoy: el filtro 'todos' los excluye a propósito) y
 * `sinValidar` sale aparte: es el número de esa fila que conviene vigilar.
 */
export function conteoPorEtapa(reportes) {
  const c = Object.fromEntries(ETAPAS.map((e) => [e, 0]))
  c.sinValidar = 0
  c.archivados = 0
  for (const r of reportes || []) {
    const e = etapaDe(r)
    if (!e) continue
    if (r.estado === 'archivado') { c.archivados += 1; continue }
    c[e] += 1
    if (r.estado === 'cerrado_sin_validar') c.sinValidar += 1
  }
  return c
}
export { TABLA as _TABLA, DEFAULT_EN_MARCHA as _DEFAULT_EN_MARCHA }

// ── Las TRES clases de un caso en la mesa (flujo v2, Mariano 3-sep-2026) ──
// La card no adivina: lee lo que la lectura del asistente ya trae.
//   'cerrar'   → la sugerencia es cerrar (el comité verificó que está resuelto)
//   'mano'     → quienEjecuta es vos/juntos: hay una mano tuya afuera del sistema
//   'decision' → lo demás (incluida la card sin lectura: la genérica)
// Sirve para la card (encabezado, botón resaltado, qué mostrar) y para el
// subfiltro de "Decidir" (despachar los cerrables de un toque).
export const CLASES_MESA = ['decision', 'cerrar', 'mano']

/** PURO. Clase de un `proximoPasoDe(r)`; null si el caso no es del grupo decidir. */
export function claseMesaDe(pp) {
  if (!pp || pp.grupo !== 'decidir') return null
  if (pp.fuente !== 'asistente') return 'decision'
  if ((pp.opciones || []).some((o) => o.sugerida && o.accion === 'cerrar')) return 'cerrar'
  if (pp.quienEjecuta === 'vos' || pp.quienEjecuta === 'juntos') return 'mano'
  return 'decision'
}

export const CLASE_MESA_META = Object.freeze({
  decision: { label: 'Decisión tuya', icono: '🧭', pregunta: 'Tu decisión (viaja con el botón que toques, al hilo y al motivo)' },
  cerrar:   { label: 'Para cerrar',   icono: '✅', pregunta: 'Opcional: por qué no cerrás, si no cerrás' },
  mano:     { label: 'Mano tuya',     icono: '🧍', pregunta: 'Qué hiciste / qué salió' },
})

// Etiquetas que cambian de tono según la clase (misma acción, mismo destino:
// sólo el texto). Lo que no está acá conserva la etiqueta de la tabla.
const ETIQUETAS_POR_CLASE = Object.freeze({
  cerrar: { cerrar: 'Cerrar — ya está resuelto', a_guardia: 'No está resuelto — a la guardia' },
  mano:   { cerrar: 'Ya lo hice — cerrar', a_guardia: 'Lo hice, falta algo — a la guardia' },
})

/**
 * PURO. Las opciones de la card para su clase: etiqueta por clase y la
 * resaltada según la clase (cerrar/mano → `cerrar`; decision → la sugerida).
 * En la clase 'cerrar' el comité no necesita "que lo piense el comité": ya lo pensó.
 */
export function opcionesParaClase(pp) {
  const clase = claseMesaDe(pp)
  if (!clase || !pp) return pp?.opciones || []
  const etiquetas = ETIQUETAS_POR_CLASE[clase] || {}
  return (pp.opciones || [])
    .filter((o) => !(clase === 'cerrar' && o.accion === 'a_comite'))
    .map((o) => ({
      ...o,
      label: etiquetas[o.accion] || o.label,
      sugerida: clase === 'decision' ? o.sugerida : o.accion === 'cerrar',
    }))
}

/**
 * PURO. Cómo se EJECUTA cada acción del vocabulario: o un cambio de estado
 * (`{estado}`) o un handler especial de la página (`{especial}`). Vive acá y
 * no en el JSX para que la card sea puro render — y para que el test pueda
 * afirmar que TODA acción de TODA opción de la tabla tiene ejecución.
 *
 * `aprobar` y `rechazar` son especiales porque no son transiciones simples:
 * aprobar pasa por Puerta Cero (step-up) y rechazar exige motivo.
 */
export function ejecucionDe(accion) {
  const MAPA = {
    aprobar: { especial: 'aprobar' },
    rechazar: { especial: 'rechazar' },
    promover: { especial: 'promover' },
    a_mesa: { estado: 'en_mesa' },
    a_comite: { estado: 'en_comite' },
    a_investigar: { estado: 'en_investigacion' },
    a_guardia: { estado: 'nuevo' },
    quedo_bien: { estado: 'cerrado' },
    cerrar: { estado: 'cerrado' },
    sigue_con_errores: { estado: 'sigue_con_errores' },
    archivar: { estado: 'archivado' },
    reabrir: { estado: 'nuevo' },
    descartar: { estado: 'descartado' },
  }
  return MAPA[accion] || null
}

// ── R7b (ADR 2026-09-07): Puerta Cero por grupos ────────────────────────────
// Los informes que el triage marcó como candidatos a duplicado entre sí
// (`triage.duplicadoDe`, cadena) se aprueban como UN solo arreglo: el
// principal entra a la cola y los demás quedan descartados como duplicado
// confirmado. Sólo por `duplicadoDe` — agrupar por locus acá sería una segunda
// implementación de `candidatosDuplicado` (functions/lib/triage/clasificador.js),
// en otro runtime. Lo creado después del triage nocturno se agrupa mañana.
const tsMsDe = (v) => {
  if (!v) return null
  if (typeof v.toMillis === 'function') return v.toMillis()
  if (v instanceof Date) return v.getTime()
  if (Number.isFinite(v?.seconds)) return v.seconds * 1000
  if (Number.isFinite(v?._seconds)) return v._seconds * 1000
  return null
}

/**
 * PURO. El grupo de duplicados (todos en `informe`) al que pertenece `reporte`.
 * @returns {{ principalId: string, miembros: string[], ids: string[] } | null}
 *   `miembros` = los que NO son el principal (los que se descartan al aprobar).
 *   `null` si el reporte no está en `informe` o el grupo es de uno.
 */
export function grupoDuplicadosDe(reporte, reportes) {
  if (!reporte || reporte.estado !== 'informe') return null
  const informes = (reportes || []).filter((r) => r && r.id && r.estado === 'informe')
  const porId = new Map(informes.map((r) => [r.id, r]))
  if (!porId.has(reporte.id)) porId.set(reporte.id, reporte)
  // Union-find chico sobre las aristas duplicadoDe (cadena, en cualquier dirección).
  const padre = new Map([...porId.keys()].map((id) => [id, id]))
  const raiz = (x) => { while (padre.get(x) !== x) { padre.set(x, padre.get(padre.get(x))); x = padre.get(x) } return x }
  for (const r of porId.values()) {
    const d = r.triage && r.triage.duplicadoDe
    if (d && porId.has(d)) padre.set(raiz(r.id), raiz(d))
  }
  const mia = raiz(reporte.id)
  const ids = [...porId.keys()].filter((id) => raiz(id) === mia)
  if (ids.length < 2) return null
  const principalId = ids
    .map((id) => ({ id, ms: tsMsDe(porId.get(id).creadoEn) }))
    .sort((a, b) => (a.ms ?? Infinity) - (b.ms ?? Infinity) || a.id.localeCompare(b.id))[0].id
  return { principalId, miembros: ids.filter((id) => id !== principalId), ids }
}

